import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Link as MuiLink,
} from '@mui/material';
import { Search as SearchIcon, OpenInNew as OpenInNewIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTenant } from '../providers/TenantProvider';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
} from 'firebase/firestore';

interface Invoice {
  id: string;
  tenantId: string;
  stripeCustomerId: string;
  stripeSubscriptionId?: string;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  periodStart: any;
  periodEnd: any;
  dueDate?: any;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
  metadata?: Record<string, any>;
  createdAt: any;
  updatedAt: any;
}

interface Member {
  id: string;
  contact: {
    firstName: string;
    lastName: string;
    email: string;
  };
  customerId?: string;
  stripeCustomerId?: string;
}

type InvoiceStatus = 'all' | 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';

export default function InvoicesPage() {
  const { tenantId, role } = useTenant();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [members, setMembers] = useState<Map<string, Member>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [backfillingInvoiceId, setBackfillingInvoiceId] = useState<string | null>(null);

  const canBackfill = role === 'owner' || role === 'manager';

  // Load members for lookup
  useEffect(() => {
    if (!tenantId) return;

    const loadMembers = async () => {
      try {
        const membersQuery = query(
          collection(db, 'members'),
          where('tenantId', '==', tenantId)
        );
        const membersSnapshot = await getDocs(membersQuery);
        const membersMap = new Map<string, Member>();
        
        membersSnapshot.forEach((doc) => {
          const memberData = doc.data() as Member;
          const memberWithId = { ...memberData, id: doc.id };
          membersMap.set(doc.id, memberWithId);
          // Also index by customerId for easy lookup
          const customerId = memberData.stripeCustomerId || memberData.customerId;
          if (customerId) {
            membersMap.set(customerId, memberWithId);
          }
        });
        
        setMembers(membersMap);
      } catch (err) {
        console.error('Error loading members:', err);
      }
    };

    loadMembers();
  }, [tenantId]);

  // Load invoices with real-time updates
  useEffect(() => {
    if (!tenantId) return;

    setLoading(true);
    setError(null);

    const invoicesQuery = query(
      collection(db, 'invoices'),
      where('tenantId', '==', tenantId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      invoicesQuery,
      (snapshot) => {
        const invoicesData: Invoice[] = [];
        snapshot.forEach((doc) => {
          invoicesData.push({ id: doc.id, ...doc.data() } as Invoice);
        });
        setInvoices(invoicesData);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading invoices:', err);
        setError('Failed to load invoices');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tenantId]);

  const handleBackfillInvoice = async (invoiceId: string) => {
    if (!canBackfill) {
      setError('You do not have permission to backfill invoices');
      return;
    }

    try {
      setBackfillingInvoiceId(invoiceId);
      setError(null);

      const backfillInvoice = httpsCallable(functions, 'backfillInvoice');
      await backfillInvoice({ tenantId, invoiceId });

      // Invoice will be updated via real-time listener
    } catch (err: any) {
      console.error('Error backfilling invoice:', err);
      setError(err.message || 'Failed to backfill invoice data');
    } finally {
      setBackfillingInvoiceId(null);
    }
  };

  const getMemberByCustomerId = (customerId: string): Member | undefined => {
    return members.get(customerId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'open':
        return 'warning';
      case 'draft':
        return 'default';
      case 'void':
      case 'uncollectible':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Filter and search invoices
  const filteredInvoices = invoices.filter((invoice) => {
    // Status filter
    if (statusFilter !== 'all' && invoice.status !== statusFilter) {
      return false;
    }

    // Search by member name or email
    if (searchQuery.trim()) {
      const member = getMemberByCustomerId(invoice.stripeCustomerId);
      if (!member) return false;

      const query = searchQuery.toLowerCase();
      const fullName = `${member.contact.firstName} ${member.contact.lastName}`.toLowerCase();
      const email = member.contact.email.toLowerCase();

      return fullName.includes(query) || email.includes(query);
    }

    return true;
  });

  const isMissingData = (invoice: Invoice) => {
    return !invoice.hostedInvoiceUrl || !invoice.invoicePdf;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Invoices
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage all invoices from Stripe
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setStatusFilter(event.target.value as InvoiceStatus)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="void">Void</MenuItem>
            <MenuItem value="uncollectible">Uncollectible</MenuItem>
          </TextField>

          <TextField
            label="Search by Member"
            value={searchQuery}
            onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(event.target.value)}
            placeholder="Name or email..."
            sx={{ flexGrow: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* Invoices Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice ID</TableCell>
              <TableCell>Member</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Amount Due</TableCell>
              <TableCell align="right">Amount Paid</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInvoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    {searchQuery || statusFilter !== 'all'
                      ? 'No invoices match your filters'
                      : 'No invoices yet'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredInvoices.map((invoice) => {
                const member = getMemberByCustomerId(invoice.stripeCustomerId);
                const missingData = isMissingData(invoice);

                return (
                  <TableRow key={invoice.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {invoice.id.slice(0, 16)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {member ? (
                        <Box>
                          <Typography variant="body2">
                            {member.contact.firstName} {member.contact.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {member.contact.email}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {invoice.stripeCustomerId.slice(0, 16)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status.toUpperCase()}
                        color={getStatusColor(invoice.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(invoice.amountDue, invoice.currency)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(invoice.amountPaid, invoice.currency)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(invoice.dueDate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        {invoice.hostedInvoiceUrl && /^https:\/\//.test(invoice.hostedInvoiceUrl) ? (
                          <Tooltip title="View Invoice">
                            <IconButton
                              size="small"
                              component={MuiLink}
                              href={invoice.hostedInvoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Invoice URL not available">
                            <Box component="span">
                              <IconButton size="small" disabled>
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        )}

                        {missingData && canBackfill && (
                          <Tooltip title="Backfill missing data from Stripe">
                            <Box component="span">
                              <IconButton
                                size="small"
                                onClick={() => handleBackfillInvoice(invoice.id)}
                                disabled={backfillingInvoiceId === invoice.id}
                                color="primary"
                              >
                                {backfillingInvoiceId === invoice.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <RefreshIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredInvoices.length} of {invoices.length} invoices
        </Typography>
      </Paper>
    </Container>
  );
}
